import {
  listStaffTemplateCategories,
  upsertStaffTemplateCategory,
} from "~/api/staff"
import { loadTemplateCategoryRecords } from "~/common/videoTemplate/categories"

export type CategoryOrder = {
  categories: string[]
  baseline: API.VideoTemplate.Category[]
  retry?: boolean
}

export const saveCategoryOrder = async (
  { categories, baseline, retry = false }: CategoryOrder,
  signal: AbortSignal,
): Promise<void> => {
  const records = await loadTemplateCategoryRecords(
    { signal },
    (params, options) => listStaffTemplateCategories(params, options?.signal),
  )
  const current = new Map(records.map((item) => [item.category, item.rank]))
  const previous = new Map(baseline.map((item) => [item.category, item.rank]))
  if (
    categories.length !== current.size ||
    categories.length !== previous.size ||
    new Set(categories).size !== categories.length ||
    categories.some(
      (category) => !current.has(category) || !previous.has(category),
    )
  )
    throw new Error("分类列表已变化，请取消排序后重新调整")

  const updates = categories.flatMap((category, index) => {
    const rank = index + 1
    if (
      current.get(category) !== previous.get(category) &&
      !(retry && current.get(category) === rank)
    )
      throw new Error("分类顺序已被修改，请取消排序后重新调整")
    return current.get(category) === rank ? [] : [{ category, rank }]
  })
  const saveBatch = async (offset: number): Promise<void> => {
    signal.throwIfAborted()
    const batch = updates.slice(offset, offset + 4)
    if (!batch.length) return
    const results = await Promise.allSettled(
      batch.map(async (item) => {
        const { data } = await upsertStaffTemplateCategory(item, signal)
        if (
          data?.category !== item.category ||
          (data.rank !== undefined && data.rank !== item.rank)
        )
          throw new Error("分类排序返回数据无效")
      }),
    )
    const failure = results.find((result) => result.status === "rejected")
    if (failure) {
      const reason =
        failure.reason instanceof Error ? failure.reason.message : "请求失败"
      throw new Error(
        `顺序保存失败：${reason}。部分分类可能已更新，请重试保存，或取消并重新加载。`,
      )
    }
    return saveBatch(offset + batch.length)
  }
  return saveBatch(0)
}
